import type { UsageReport } from '@deskworks/shared-types';
import { useState } from 'react';
import { intlLocale, t } from '@/lib/i18n';
import { formatTokens, formatUsd } from '@/lib/usage';
import { cn } from '@/lib/utils';

export type UsageMetric = 'cost' | 'tokens';

/** Categorical slots 1–2 of the validated chart palette (light / dark steps). */
const SERIES = [
  'bg-[#2a78d6] dark:bg-[#3987e5]',
  'bg-[#eb6834] dark:bg-[#d95926]',
] as const;

const dayFmt = new Intl.DateTimeFormat(intlLocale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dayLabel = (date: string) => dayFmt.format(new Date(`${date}T00:00:00Z`));

/** Daily stacked bars: API spend + subscription value, or input + output tokens. */
export function UsageChart({ daily, metric }: { daily: UsageReport['daily']; metric: UsageMetric }) {
  const [hover, setHover] = useState<number | null>(null);
  const series =
    metric === 'cost'
      ? [
          { label: t('API spend'), value: (d: UsageReport['daily'][number]) => d.apiCostUsd },
          { label: t('Subscription value'), value: (d: UsageReport['daily'][number]) => d.subscriptionValueUsd },
        ]
      : [
          { label: t('Input tokens'), value: (d: UsageReport['daily'][number]) => d.inputTokens },
          { label: t('Output tokens'), value: (d: UsageReport['daily'][number]) => d.outputTokens },
        ];
  const format = metric === 'cost' ? formatUsd : (n: number) => formatTokens(n);
  const totals = daily.map((d) => series[0].value(d) + series[1].value(d));
  const max = Math.max(0, ...totals);
  const active = hover !== null ? daily[hover] : null;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {series.map((s, i) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span className={cn('size-2.5 rounded-[3px]', SERIES[i])} aria-hidden />
            {s.label}
          </span>
        ))}
        <span className="ml-auto tabular-nums">{t('peak {value} / day', { value: format(max) })}</span>
      </div>

      <div className="relative">
        {max === 0 ? (
          <div className="flex h-40 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
            {t('Nothing used in this period.')}
          </div>
        ) : (
          <div className="flex h-40 items-end gap-[2px] border-b border-border" onMouseLeave={() => setHover(null)}>
            {daily.map((d, i) => {
              const a = series[0].value(d);
              const b = series[1].value(d);
              const height = ((a + b) / max) * 100;
              return (
                <div
                  key={d.date}
                  className="group flex h-full min-w-0 flex-1 cursor-default flex-col justify-end"
                  onMouseEnter={() => setHover(i)}
                  aria-label={`${dayLabel(d.date)}: ${series[0].label} ${format(a)}, ${series[1].label} ${format(b)}`}
                  role="img"
                >
                  <div className={cn('flex flex-col-reverse gap-[2px]', hover === i && 'opacity-80')} style={{ height: `${height}%` }}>
                    {a > 0 && <div className={cn('min-h-px', SERIES[0], b === 0 && 'rounded-t-[4px]')} style={{ flexGrow: a }} />}
                    {b > 0 && <div className={cn('min-h-px rounded-t-[4px]', SERIES[1])} style={{ flexGrow: b }} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {active && hover !== null && (
          <div
            className="pointer-events-none absolute -top-2 z-10 w-max -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md"
            style={{ left: `${((hover + 0.5) / daily.length) * 100}%` }}
          >
            <div className="mb-0.5 font-medium">{dayLabel(active.date)}</div>
            {series.map((s, i) => (
              <div key={s.label} className="flex items-center gap-1.5 text-muted-foreground">
                <span className={cn('size-2 rounded-[2px]', SERIES[i])} aria-hidden />
                {s.label}: <span className="text-foreground tabular-nums">{format(s.value(active))}</span>
              </div>
            ))}
            <div className="text-muted-foreground">{active.runs === 1 ? t('1 run') : t('{n} runs', { n: active.runs.toLocaleString(intlLocale) })}</div>
          </div>
        )}
      </div>

      {daily.length > 0 && (
        <div className="mt-1.5 flex justify-between text-[11px] text-muted-foreground tabular-nums">
          <span>{dayLabel(daily[0].date)}</span>
          {daily.length > 2 && <span>{dayLabel(daily[Math.floor(daily.length / 2)].date)}</span>}
          <span>{dayLabel(daily[daily.length - 1].date)}</span>
        </div>
      )}
    </div>
  );
}
